import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBellSlash, faPencil, faSliders, faTrash } from '@fortawesome/free-solid-svg-icons';

import {
  Contact,
  deviceLabelFor as contactDeviceLabel,
  ReceivedBroadcast,
  Subscription,
} from '@/core/notifications/model';
import NotificationRow, {
  NOTIFICATION_ROW_DIVIDER_STYLE,
} from '@/core/notifications/components/settings/notification-row';
import SubscriptionFilterPanel from '@/core/notifications/components/settings/subscription-filter-panel';
import usePushSubscription from '@/core/notifications/hooks/use-push-subscription';
import { renameDeviceContact } from '@/core/notifications/services/push-subscription-service';
import { guessDeviceLabel } from '@/shared/util/export';
import { Toggle, AppModal } from '@/shared/components/export';

const THIS_DEVICE_ID = 'this-device';

export interface SourcesData {
  subscriptions: Subscription[];
  broadcasts: ReceivedBroadcast[];
}

function deviceLabelFor(contact: Contact | undefined): string | undefined {
  return contact ? contactDeviceLabel(contact) : undefined;
}

// Defensive against a transient duplicate for the same endpoint (e.g. a
// rename's delete-then-recreate racing a list refresh) — always render at
// most one row per endpoint, preferring the most recently created contact.
function dedupeByEndpoint(contacts: Contact[]): Contact[] {
  const byEndpoint = new Map<string, Contact>();
  for (const contact of contacts) {
    const endpoint = contact.config.endpoint as string | undefined;
    if (!endpoint) continue;
    const current = byEndpoint.get(endpoint);
    if (!current || (contact.createdAt ?? '') >= (current.createdAt ?? '')) {
      byEndpoint.set(endpoint, contact);
    }
  }
  return [...byEndpoint.values()];
}

export default function PushChannelRow({
  contacts,
  onRequestDelete,
  onContactsChanged,
  sourcesData,
  loadSources,
}: {
  contacts: Contact[];
  onRequestDelete: (contact: Contact) => void;
  onContactsChanged: () => Promise<void>;
  sourcesData: SourcesData | null;
  loadSources: () => void;
}) {
  const { status, errorMessage, busy, currentDeviceEndpoint, enable, disable, rename, refresh } =
    usePushSubscription(contacts);
  const [preEnableLabel, setPreEnableLabel] = useState(() => guessDeviceLabel());
  const [filterPanelContactId, setFilterPanelContactId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const dedupedContacts = dedupeByEndpoint(contacts);
  const thisDeviceContact = dedupedContacts.find(
    contact => contact.config.endpoint === currentDeviceEndpoint,
  );
  const thisDeviceLabel = deviceLabelFor(thisDeviceContact) ?? guessDeviceLabel();
  const otherDeviceContacts = dedupedContacts.filter(
    contact => contact.config.endpoint !== currentDeviceEndpoint,
  );

  const handleToggle = async () => {
    if (status === 'on') {
      await disable();
    } else {
      await enable(preEnableLabel);
    }
    await onContactsChanged();
  };

  const openRename = (id: string, currentLabel: string) => {
    setRenamingId(id);
    setRenameValue(currentLabel);
  };

  const closeRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const handleRenameSave = async () => {
    const label = renameValue.trim();
    if (!renamingId || !label) return;
    setSavingRename(true);
    try {
      if (renamingId === THIS_DEVICE_ID) {
        const ok = await rename(label);
        if (ok) await onContactsChanged();
      } else {
        const contact = otherDeviceContacts.find(c => c.id === renamingId);
        if (contact) {
          await renameDeviceContact(contact, label);
          await onContactsChanged();
        }
      }
      closeRename();
    } finally {
      setSavingRename(false);
    }
  };

  const toggleFilterPanel = (contactId: string) => {
    if (filterPanelContactId === contactId) {
      setFilterPanelContactId(null);
      return;
    }
    loadSources();
    setFilterPanelContactId(contactId);
  };

  const handleFilterSaved = async () => {
    setFilterPanelContactId(null);
    await onContactsChanged();
  };

  return (
    <div>
      {(status === 'unsupported' || status === 'needs-install') && (
        <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faBellSlash} className="mutedText" />
            <span className="mutedText" style={{ fontSize: 14 }}>
              {status === 'unsupported'
                ? "Push notifications aren't supported in this browser."
                : 'Install this app to your Home Screen (iOS) or Dock (macOS) to enable push notifications.'}
            </span>
          </div>
        </NotificationRow>
      )}

      {status === 'checking' && (
        <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
          <span className="mutedText" style={{ fontSize: 14 }}>
            Checking push status…
          </span>
        </NotificationRow>
      )}

      {status === 'permission-denied' && (
        <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
          <div className="d-flex align-items-center justify-content-between w-100">
            <span style={{ fontSize: 14, color: 'var(--bs-warning)' }}>
              Notifications are blocked for this site. Enable them in your browser&apos;s site
              settings, then retry.
            </span>
            <button className="btn btn-sm btn-outline-secondary" onClick={refresh}>
              Try again
            </button>
          </div>
        </NotificationRow>
      )}

      {(status === 'off' || status === 'on' || status === 'error') && (
        <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
          <div className="w-100">
            <div className="d-flex align-items-center justify-content-between gap-2">
              {status === 'on' ? (
                <span
                  className="text-truncate flex-grow-1"
                  style={{ fontSize: 14, minWidth: 0 }}
                  title={thisDeviceLabel}
                >
                  {thisDeviceLabel}
                </span>
              ) : (
                // Not enabled yet: the name is editable up front, saved once toggled on.
                <input
                  className="form-control form-control-sm flex-grow-1"
                  style={{ minWidth: 0 }}
                  value={preEnableLabel}
                  disabled={busy}
                  onChange={event => setPreEnableLabel(event.target.value)}
                />
              )}
              {status === 'on' && (
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <span
                    className="flex-shrink-0"
                    style={{
                      fontSize: 11,
                      borderRadius: 2,
                      padding: '2px 6px',
                      fontWeight: 600,
                      background: 'rgba(var(--bs-info-rgb), 0.15)',
                      color: 'var(--bs-info)',
                    }}
                  >
                    This device
                  </span>
                  <button
                    className="btn btn-sm p-0 border-0 bg-transparent flex-shrink-0"
                    aria-label="Rename this device"
                    title="Rename this device"
                    onClick={() => openRename(THIS_DEVICE_ID, thisDeviceLabel)}
                  >
                    <FontAwesomeIcon icon={faPencil} className="mutedText" size="xs" />
                  </button>
                  {thisDeviceContact && (
                    <button
                      className="btn btn-sm p-0 border-0 bg-transparent flex-shrink-0"
                      aria-label="Manage what this device receives"
                      title="Manage what this device receives"
                      onClick={() => toggleFilterPanel(thisDeviceContact.id)}
                    >
                      <FontAwesomeIcon icon={faSliders} className="mutedText" size="xs" />
                    </button>
                  )}
                </div>
              )}
              <Toggle checked={status === 'on'} onChange={handleToggle} disabled={busy} />
            </div>
            {status === 'error' && errorMessage && (
              <div style={{ fontSize: 12, color: 'var(--bs-danger)' }}>{errorMessage}</div>
            )}
          </div>
        </NotificationRow>
      )}

      {thisDeviceContact && filterPanelContactId === thisDeviceContact.id && sourcesData && (
        <SubscriptionFilterPanel
          contact={thisDeviceContact}
          subscriptions={sourcesData.subscriptions}
          broadcasts={sourcesData.broadcasts}
          onSaved={handleFilterSaved}
          onCancel={() => setFilterPanelContactId(null)}
        />
      )}

      {otherDeviceContacts.map(contact => (
        <div key={contact.id}>
          <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
            <span className="text-truncate flex-grow-1" style={{ fontSize: 15, minWidth: 0 }}>
              {deviceLabelFor(contact) ?? contact.identityValue}
            </span>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <button
                className="btn btn-sm p-0 border-0 bg-transparent"
                aria-label="Rename device"
                title="Rename device"
                onClick={() => openRename(contact.id, deviceLabelFor(contact) ?? '')}
              >
                <FontAwesomeIcon icon={faPencil} className="mutedText" size="xs" />
              </button>
              <button
                className="btn btn-sm p-0 border-0 bg-transparent"
                aria-label="Manage what this device receives"
                title="Manage what this device receives"
                onClick={() => toggleFilterPanel(contact.id)}
              >
                <FontAwesomeIcon icon={faSliders} className="mutedText" size="xs" />
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ padding: '3px 8px', lineHeight: 1 }}
                onClick={() => onRequestDelete(contact)}
                aria-label="Delete"
                title="Delete"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </NotificationRow>
          {filterPanelContactId === contact.id && sourcesData && (
            <SubscriptionFilterPanel
              contact={contact}
              subscriptions={sourcesData.subscriptions}
              broadcasts={sourcesData.broadcasts}
              onSaved={handleFilterSaved}
              onCancel={() => setFilterPanelContactId(null)}
            />
          )}
        </div>
      ))}

      <AppModal
        show={renamingId !== null}
        onClose={closeRename}
        icon={faPencil}
        title="Rename Device"
        primaryLabel={savingRename ? 'Saving…' : 'Save'}
        onPrimary={handleRenameSave}
        primaryDisabled={savingRename || !renameValue.trim()}
      >
        <label className="form-label">Device name</label>
        <input
          className="form-control form-control-sm"
          autoFocus
          value={renameValue}
          onChange={event => setRenameValue(event.target.value)}
          onKeyDown={event => event.key === 'Enter' && handleRenameSave()}
        />
      </AppModal>
    </div>
  );
}
